import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { Button, Text } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { InputForm } from '../../inputs/forms/InputForm';
import { FormBox } from '../../formBox';
import { FormHeader } from '../../FormHeader';
import { IGroup, useGroups } from '../../../hooks/useGroups';

const schema = yup.object().shape({
  nome: yup.string().required('Este campo é obrigatório!'),
});

interface IForm {
  nome: string;
}

interface IDefaultValues extends IForm {
  id?: number;
}

interface GroupsFormProps {
  defaultValues?: IDefaultValues;
}

// O formulário só cuida do nome. Telas, ações, carteira e permissões gerais do
// grupo ficam na Central de Permissões (antes um campo "Permissões gerais" aqui
// alternava cada permissão e, ao salvar o nome, apagava as que o grupo tinha).
export default function GroupsForm({ defaultValues }: GroupsFormProps) {
  const navigate = useNavigate();
  const { createGroup, updateGroup } = useGroups();
  const { mutateAsync } = useMutation({
    mutationFn: defaultValues ? updateGroup : createGroup,
  });

  const formMethods = useForm<IForm>({
    resolver: yupResolver(schema),
    defaultValues: { nome: defaultValues?.nome ?? '' },
  });

  const onSubmit = async (values: IForm) => {
    try {
      const response = await mutateAsync({ nome: values.nome, id: defaultValues?.id } as IGroup);
      toast.success(`Grupo ${defaultValues ? 'editado' : 'criado'} com sucesso!`, {
        position: 'top-right',
      });
      if (defaultValues) navigate('/configuracoes/grupos', { replace: true });
      else navigate(`/configuracoes/permissoes?grupo=${response.data.id}`, { replace: true });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ?? 'Erro interno no sistema, tente novamente mais tarde.';
      toast.error(errorMessage, { position: 'top-right' });
    }
  };

  useEffect(() => {
    if (defaultValues?.nome) {
      formMethods.reset({ nome: defaultValues.nome });
    }
  }, [defaultValues]);

  return (
    <form onSubmit={formMethods.handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
        <FormHeader
          title="Grupos"
          subtitle="Crie grupos de trabalho. O que cada grupo pode ver e fazer fica na Central de Permissões."
        />

        <FormBox>
          <FormProvider {...formMethods}>
            <InputForm name="nome" label="Nome" placeholder="Nome do grupo" />
          </FormProvider>
          <Text fontSize="13px" color="gray.500">
            {defaultValues
              ? 'As permissões deste grupo ficam na Central de Permissões.'
              : 'Depois de criar, você vai direto para a Central de Permissões liberar as telas do grupo.'}
          </Text>
          {defaultValues?.id && (
            <Button
              variant="outline"
              size="sm"
              alignSelf="flex-start"
              onClick={() => navigate(`/configuracoes/permissoes?grupo=${defaultValues.id}`)}
            >
              Abrir na Central de Permissões
            </Button>
          )}
        </FormBox>
      </div>

      <div className="mt-3 border border-b-divider" />

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => navigate('/configuracoes/grupos')}>
          Cancelar
        </Button>
        <Button type="submit">Salvar</Button>
      </div>
    </form>
  );
}
